import {
  ArcElement,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  Tooltip,
} from "chart.js";
import ChartDataLabels, { Context } from "chartjs-plugin-datalabels";
import { FC, ReactNode } from "react";
import { Pie } from "react-chartjs-2";
import { useRecoilValue } from "recoil";
import styled, { css } from "styled-components";

import { useRtl } from "@ryde/redux";
import {
  contextManipulation,
  currencySignSelector,
  DashboardCardNameEnum,
  DashboardCardTemplateTypeEnum,
  DashboardGraphColorMappingEnum,
  DATA_E2E_KEY,
  IPieGraphContent,
  IPieGraphSettings,
  IStyledAttributes,
  MIN_ARC_PERCENT_FOR_LABEL_SHOW_IN_PIE,
  pickGraphColors,
  useFormats,
} from "@ryde/shared";
import { rydeColors, rydeColorsDashboardPage } from "@ryde/theme";

const PieContentStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "dashboard-card-pie-content",
})`
  height: 100%;
  display: grid;
  grid-template-areas:
    "padding1 padding2"
    "piechart legend"
    "more area";
  grid-template-columns: 30% 70%;
  grid-template-rows: auto 80% auto;
`;

const PieContainerStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "dashboard-card-pie-container",
})<{ $position: boolean }>`
  grid-area: piechart;
  display: flex;
  padding-bottom: 10%;
  ${({ $position }) => {
    if ($position) {
      return { justifyContent: "end" };
    }

    return { justifyContent: "start" };
  }};
  align-self: center;

  @media (max-width: 1900px) {
    padding-bottom: 15%;
  }
`;
const LegendContainerStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "dashboard-card-pie-legend-container",
})<{ $shouldScroll: boolean }>`
  height: 90%;
  grid-area: legend;
  ${({ $shouldScroll }) =>
    $shouldScroll &&
    css`
      overflow-y: scroll;
    `};
  align-content: center;
  padding-inline-start: 20px;

  @media (max-width: 1900px) {
    padding-bottom: 15%;
  }
`;
const LegendTableStyled = styled.table`
  table-layout: auto;
  width: 100%;
  @media (max-width: 768px) {
    padding-bottom: 5%;
    align-self: center;
  }
`;
const LegendRowStyled = styled.tr.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "dashboard-card-pie-legend-row",
})`
  margin-top: 10px;
`;
const LegendCellStyled = styled.td.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "dashboard-card-pie-legend-cell",
})`
  font-size: 14px;
  border-bottom: 1px dashed
    ${rydeColorsDashboardPage.dashboardHeaderBorderColor};
`;
export interface IPieGraphContentProps {
  cardType: DashboardCardTemplateTypeEnum;
  cardName: DashboardCardNameEnum;
  colorScheme: DashboardGraphColorMappingEnum;
  content: IPieGraphContent;
  settings: IPieGraphSettings;
}
export interface IPieGraphLegendContentProps {
  cardName: DashboardCardNameEnum;
  content: Partial<IPieGraphContent>[];
  settings: IPieGraphSettings;
  colors: string[];
}
export interface IPieGraphLegendRowProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rowItem: Record<string, any>; // TODO PieAPILegendParams;
}
export interface IPieGraphLegendCellProps {
  fieldName: string;
  fieldValue: string;
}

export const CardPieContentTemplate: FC<IPieGraphContentProps> = ({
  cardType = DashboardCardTemplateTypeEnum.PIE_CHART,
  cardName = DashboardCardNameEnum.NONE,
  colorScheme = DashboardGraphColorMappingEnum.DIFFERENT_COLORS_WITH_CONDITION,
  content,
  settings,
}) => {
  // TODO check this function it fires on each render
  ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);
  const { getNumberWithCurrencyNormalized, getNumberWithCommasNormalized } =
    useFormats();
  const currencySign = useRecoilValue(currencySignSelector);

  const { isRtl } = useRtl();
  const colors = pickGraphColors(
    cardName,
    colorScheme,
    settings.fieldName,
    settings.fieldValueToGetSecondaryValue,
    content.arcs.map(ele => ele.params),
    settings.predicateFunctionColor,
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const options: ChartOptions<any> = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        color: function (context: Context) {
          return contextManipulation<string>(
            context,
            MIN_ARC_PERCENT_FOR_LABEL_SHOW_IN_PIE,
            rydeColors.white,
            rydeColors.black,
          );
        },
        display: function (context: Context) {
          return contextManipulation<boolean>(
            context,
            MIN_ARC_PERCENT_FOR_LABEL_SHOW_IN_PIE,
            true,
            false,
          );
        },
        font: {
          size: 12,
        },
      },
    },
  };
  const data = {
    labels: content.arcs.map(ele => {
      const temp = {
        value: ele.value,
        params: {
          ...ele.params,
          totalPrice: getNumberWithCurrencyNormalized(
            getNumberWithCommasNormalized(Number(ele.params.totalPrice)),
            currencySign, //TODO : change to currency according to preference
          ),
        },
      };
      return settings.labelsFormat(temp);
    }),
    datasets: [
      {
        data: content.arcs.map(ele => ele.value),
        backgroundColor: colors,
      },
    ],
  };
  const Chart = <Pie data={data} options={options} />;
  const PieLegend = (
    <PieLegendTable
      cardName={cardName}
      content={settings.legendDataHook(
        content.arcs,
        settings.legendFieldsFromParams,
      )}
      settings={settings}
      colors={colors}
    />
  );

  /* Don't change PieContainerStyled props and style ... required for RYDE-3469 */
  return (
    <PieContentStyled>
      <PieContainerStyled dir={"ltr"} $position={isRtl}>
        {Chart}
      </PieContainerStyled>
      {PieLegend}
    </PieContentStyled>
  );
};

export const PieLegendTable: FC<IPieGraphLegendContentProps> = ({
  cardName,
  content,
  settings,
  colors,
}) => {
  const minNumRowsForScroll = 6;
  return (
    <LegendContainerStyled
      $shouldScroll={content.length >= minNumRowsForScroll}
    >
      <LegendTableStyled>
        <tbody>
          {content.map((ele, index: number) => {
            return (
              <PieLegendRow
                key={`${index}_${ele.arcs?.[0]?.value || "_"}`}
                rowItem={{ color: colors[index], ...ele }}
              />
            );
          })}
        </tbody>
      </LegendTableStyled>
    </LegendContainerStyled>
  );
};

export const PieLegendRow: FC<IPieGraphLegendRowProps> = ({ rowItem }) => {
  return (
    <LegendRowStyled>
      {Object.keys(rowItem).map(field => (
        <PieLegendCell
          key={field}
          fieldName={field}
          fieldValue={rowItem[field]}
        />
      ))}
    </LegendRowStyled>
  );
};

export const PieLegendCell: FC<IPieGraphLegendCellProps> = ({
  fieldName,
  fieldValue,
}) => {
  let Cell: ReactNode;

  if (fieldName === "color") {
    Cell = (
      <LegendCellStyled>
        <div
          style={{
            backgroundColor: fieldValue,
            width: "10px",
            height: "10px",
            display: "inline-block",
          }}
        />
      </LegendCellStyled>
    );
  } else {
    Cell = <LegendCellStyled>{fieldValue}</LegendCellStyled>;
  }

  return Cell;
};
